import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserCircle, FloppyDisk, EnvelopeSimple, Phone, MapPin, Calendar, IdentificationCard, DeviceMobile, PencilSimple, ShieldCheck, Clock, ArrowRight } from "@phosphor-icons/react";

const emptyForm = {
  name: "",
  phone: "",
  dob: "",
  bio: "",
  picture: "",
  avatar_file_id: "",
  address: "",
  city: "",
  country: "",
  gender: "",
};

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setForm({
        name: r.data.name || "",
        phone: r.data.phone || "",
        dob: r.data.dob || "",
        bio: r.data.bio || "",
        picture: r.data.picture || "",
        avatar_file_id: r.data.avatar_file_id || "",
        address: r.data.address || "",
        city: r.data.city || "",
        country: r.data.country || "",
        gender: r.data.gender || "",
      });
      setLoaded(true);
    });
  }, []);

  const completeness = React.useMemo(() => {
    const fields = ["name", "phone", "dob", "bio", "address", "city", "country", "gender"];
    const filled = fields.filter((f) => (form[f] || "").toString().trim().length > 0).length;
    return Math.round((filled / fields.length) * 100);
  }, [form]);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api.put("/users/me", form);
      toast.success("Profile updated");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <div>
      <PageHeader
        overline="Your account"
        title="Profile"
        subtitle="Keep your details up to date. This helps us personalize your dashboard and issue accurate certificates."
      />

      {/* Completeness */}
      <div className="border border-zinc-200 p-5 mb-6" data-testid="profile-completeness">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Profile completeness</div>
          <div className="font-mono-tab text-sm font-bold">{completeness}%</div>
        </div>
        <div className="h-2 bg-zinc-100">
          <div className={`h-2 ${completeness >= 80 ? "bg-green-600" : completeness >= 50 ? "bg-amber-500" : "bg-zinc-900"}`} style={{ width: `${completeness}%` }} />
        </div>
        {completeness < 100 && (
          <div className="text-xs text-zinc-500 mt-2">Complete your profile to unlock personalized recommendations.</div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Left card - identity */}
        <div className="border border-zinc-200 p-6">
          <div className="flex flex-col items-center text-center">
            <AvatarPreview form={form} name={user?.name} />
            <div className="mt-4 font-display text-lg font-bold">{form.name || user?.name}</div>
            <div className="text-xs text-zinc-500 mt-1 inline-flex items-center gap-1">
              <EnvelopeSimple size={11} /> {user?.email}
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-[0.2em] border border-zinc-300 px-2 py-1">
              {user?.role?.replace("_", " ")}
            </div>
          </div>
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 font-semibold">Profile picture</div>
            <AvatarUploader
              fileId={form.avatar_file_id}
              onChange={(id) => setForm({ ...form, avatar_file_id: id })}
            />
            <div className="text-[10px] text-zinc-500 mt-3 mb-1 uppercase tracking-[0.2em] font-semibold">Or use an external URL</div>
            <input
              value={form.picture}
              onChange={(e) => setForm({ ...form, picture: e.target.value })}
              placeholder="https://…"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="profile-picture-input"
            />
            <div className="text-[10px] text-zinc-500 mt-1">The uploaded picture takes precedence over the URL. Your photo also appears on issued certificates.</div>
          </div>
        </div>

        {/* Right form */}
        <div className="md:col-span-2 border border-zinc-200 p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Personal details</div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Full name" required>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="profile-name-input"
              />
            </Field>
            <Field label="Phone" icon={<Phone size={11} />}>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1 555 000 0000"
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="profile-phone-input"
              />
            </Field>
            <Field label="Date of birth" icon={<Calendar size={11} />}>
              <input
                type="date"
                value={form.dob}
                onChange={(e) => setForm({ ...form, dob: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="profile-dob-input"
              />
            </Field>
            <Field label="Gender" icon={<IdentificationCard size={11} />}>
              <select
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none bg-white"
                data-testid="profile-gender-input"
              >
                <option value="">Prefer not to say</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="non_binary">Non-binary</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="City" icon={<MapPin size={11} />}>
              <input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="profile-city-input"
              />
            </Field>
            <Field label="Country">
              <input
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="profile-country-input"
              />
            </Field>
          </div>

          <Field label="Address" className="mt-4">
            <input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Street, apartment, etc."
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="profile-address-input"
            />
          </Field>

          <Field label="Bio" className="mt-4">
            <textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              rows={3}
              placeholder="Tell us a bit about yourself…"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="profile-bio-input"
            />
          </Field>

          <div className="mt-6 flex justify-end">
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
              data-testid="profile-save-btn"
            >
              <FloppyDisk size={14} /> {saving ? "Saving…" : "Save profile"}
            </button>
          </div>
        </div>
      </div>

      {/* Account identity (role / mobile / email) */}
      <AccountIdentity user={user} onRefresh={refresh} />

      {/* Danger zone */}
      <DangerZone />
    </div>
  );
}

function AccountIdentity({ user, onRefresh }) {
  const [pendingRole, setPendingRole] = useState(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const [step, setStep] = useState("ask"); // 'ask' | 'verify'
  const [newMobile, setNewMobile] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [devCode, setDevCode] = useState(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // Load any pending role request for this user (self-view). Admins see the
  // full queue on the Users page; here we only show the user's own request.
  useEffect(() => {
    (async () => {
      try {
        // Admin endpoint; non-admins silently 403 which is fine
        const r = await api.get("/role-requests?status=pending");
        const mine = (r.data || []).find((x) => x.user_id === user?.user_id);
        if (mine) setPendingRole(mine);
      } catch { /* not an admin — skip */ }
    })();
  }, [user?.user_id]);

  const validMobile = /^[6-9]\d{9}$/.test(newMobile);

  const sendOtp = async () => {
    if (!validMobile) { toast.error("Enter a valid 10-digit Indian number"); return; }
    setBusy(true);
    try {
      const r = await api.post("/users/me/mobile/request-change", { new_mobile: newMobile });
      setStep("verify");
      setCooldown(30);
      setDevCode(r.data.dev_code || null);
      if (r.data.dev_code) toast.info(`Dev OTP: ${r.data.dev_code}`);
      else toast.success(`OTP sent to +91 ${newMobile}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send OTP");
    } finally { setBusy(false); }
  };

  const confirmOtp = async () => {
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      await api.post("/users/me/mobile/confirm-change", { new_mobile: newMobile, code });
      toast.success("Mobile number updated");
      setChangeOpen(false);
      setStep("ask");
      setNewMobile("");
      setCode("");
      setDevCode(null);
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid OTP");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-10 border border-zinc-200" data-testid="account-identity">
      <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Account</div>
          <div className="text-sm font-semibold text-zinc-900 mt-1">Sign-in identity &amp; role</div>
        </div>
        <Link
          to="/complete-profile?edit=1"
          className="inline-flex items-center gap-1.5 border border-zinc-900 text-zinc-900 px-3 py-2 text-xs font-semibold hover:bg-zinc-900 hover:text-white transition-colors"
          data-testid="profile-edit-account-btn"
        >
          <PencilSimple size={12} /> Edit account details
        </Link>
      </div>

      <div className="divide-y divide-zinc-100">
        <IdentityRow
          icon={<DeviceMobile size={14} />}
          label="Mobile"
          value={user?.mobile ? `+${user.mobile}` : "Not linked"}
          verified={!!user?.mobile_verified}
          action={
            <button
              onClick={() => { setChangeOpen((v) => !v); setStep("ask"); setNewMobile(""); setCode(""); }}
              className="text-xs font-semibold text-zinc-700 hover:text-zinc-900 underline underline-offset-2"
              data-testid="profile-change-mobile-btn"
            >
              {changeOpen ? "Cancel" : (user?.mobile ? "Change" : "Add")}
            </button>
          }
        />
        <IdentityRow icon={<EnvelopeSimple size={14} />} label="Email" value={user?.email || "Not set"} verified={!!user?.email && !!user?.email_verified} />
        <IdentityRow
          icon={<IdentificationCard size={14} />}
          label="Role"
          value={<span className="capitalize">{(user?.role || "").replace(/_/g, " ")}</span>}
          action={pendingRole && (
            <span className="text-[10px] uppercase tracking-wider border border-amber-300 bg-amber-50 text-amber-800 px-2 py-0.5 inline-flex items-center gap-1" data-testid="profile-pending-role">
              <Clock size={10} /> Pending {pendingRole.requested_role.replace(/_/g, " ")}
            </span>
          )}
        />
      </div>

      {changeOpen && (
        <div className="p-6 border-t border-zinc-200 bg-zinc-50" data-testid="mobile-change-panel">
          {step === "ask" ? (
            <div className="max-w-sm">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">New mobile number</div>
              <div className="flex items-stretch border border-zinc-300 bg-white focus-within:border-zinc-900">
                <span className="inline-flex items-center px-3 text-sm font-mono text-zinc-600 bg-zinc-100 border-r border-zinc-300">+91</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder="98765 43210"
                  value={newMobile}
                  onChange={(e) => setNewMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className="flex-1 px-3 py-2.5 text-sm focus:outline-none font-mono tracking-wider"
                  data-testid="mobile-change-input"
                />
              </div>
              <button
                onClick={sendOtp}
                disabled={!validMobile || busy}
                className="mt-4 bg-zinc-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
                data-testid="mobile-change-send-btn"
              >
                {busy ? "Sending…" : "Send OTP"}
              </button>
            </div>
          ) : (
            <div className="max-w-sm">
              <div className="text-xs text-zinc-600 mb-3">
                Code sent to <span className="font-mono text-zinc-900">+91 {newMobile}</span>
                {devCode && <> · Dev OTP: <span className="font-mono font-bold text-amber-700">{devCode}</span></>}
              </div>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full border border-zinc-300 px-3 py-3 text-lg tracking-[0.4em] font-mono text-center bg-white focus:border-zinc-900 focus:outline-none"
                placeholder="••••••"
                data-testid="mobile-change-otp-input"
              />
              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={confirmOtp}
                  disabled={busy || code.length !== 6}
                  className="bg-zinc-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
                  data-testid="mobile-change-confirm-btn"
                >
                  {busy ? "Verifying…" : "Confirm change"}
                </button>
                <button
                  onClick={sendOtp}
                  disabled={busy || cooldown > 0}
                  className="text-xs font-semibold text-zinc-700 disabled:text-zinc-400"
                  data-testid="mobile-change-resend-btn"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IdentityRow({ icon, label, value, verified, action }) {
  return (
    <div className="px-6 py-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <div className="text-zinc-400 shrink-0">{icon}</div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
          <div className="text-sm font-medium text-zinc-900 truncate flex items-center gap-2">
            {value}
            {verified && <ShieldCheck size={12} weight="fill" className="text-emerald-600 shrink-0" title="Verified" />}
          </div>
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

function DangerZone() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const isConfirmed = confirmText.trim().toUpperCase() === "DELETE";

  const submit = async () => {
    if (!isConfirmed) { toast.error("Type DELETE to confirm"); return; }
    setBusy(true);
    try {
      await api.post("/auth/delete-account", { password: password || null, confirm_text: confirmText });
      // Clear client-side session and land on landing page
      try { localStorage.removeItem("session_token"); } catch {}
      toast.success("Your account has been deleted");
      window.location.assign("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-10 border border-red-200 bg-red-50/40" data-testid="danger-zone">
      <div className="px-6 py-4 border-b border-red-200 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-red-700 font-semibold">Danger zone</div>
          <div className="text-sm font-semibold text-zinc-900 mt-1">Permanently delete your account</div>
          <div className="text-xs text-zinc-600 mt-0.5">Removes your profile, exam attempts, wallet balance and personal data. Certificates are kept but anonymized. This cannot be undone.</div>
        </div>
        {!open && (
          <button
            onClick={() => setOpen(true)}
            className="border border-red-600 text-red-700 px-3 py-2 text-xs font-semibold hover:bg-red-600 hover:text-white transition-colors flex-shrink-0"
            data-testid="delete-account-open"
          >
            Delete my account
          </button>
        )}
      </div>

      {open && (
        <div className="p-6 space-y-4" data-testid="delete-account-form">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Re-enter your password</div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password (skip if you use Google Sign-In)"
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="delete-account-password"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Type <span className="text-red-600 font-mono">DELETE</span> to confirm</div>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm font-mono tracking-widest focus:border-red-600 focus:outline-none"
              data-testid="delete-account-confirm"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <button
              onClick={submit}
              disabled={busy || !isConfirmed}
              className="bg-red-600 text-white px-4 py-2 text-sm font-semibold hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="delete-account-submit"
            >
              {busy ? "Deleting…" : "Permanently delete"}
            </button>
            <button
              onClick={() => { setOpen(false); setPassword(""); setConfirmText(""); }}
              className="border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-50"
              data-testid="delete-account-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, required, icon, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 inline-flex items-center gap-1">
        {icon} {label} {required && <span className="text-red-600">*</span>}
      </div>
      {children}
    </label>
  );
}


function avatarUrlFor(fileId) {
  if (!fileId) return "";
  const t = localStorage.getItem("session_token") || "";
  return `${(process.env.REACT_APP_BACKEND_URL || "")}/api/files/${fileId}?auth=${encodeURIComponent(t)}`;
}

function AvatarPreview({ form, name }) {
  const src = form.avatar_file_id ? avatarUrlFor(form.avatar_file_id) : (form.picture || "");
  const initial = (name || form.name || "?").trim().charAt(0).toUpperCase();
  const [err, setErr] = useState(false);
  // Reset the error state whenever the src changes (e.g. after a fresh upload).
  useEffect(() => { setErr(false); }, [src]);
  if (!src || err) {
    return (
      <div className="w-24 h-24 rounded-full flex items-center justify-center bg-zinc-900 text-white font-display text-3xl font-bold" data-testid="profile-avatar-placeholder">
        {initial}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt="Your avatar"
      className="w-24 h-24 rounded-full object-cover border-2 border-zinc-200"
      data-testid="profile-avatar-preview"
      onError={() => setErr(true)}
    />
  );
}

function AvatarUploader({ fileId, onChange }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);
  const doUpload = async (file) => {
    if (!file.type.startsWith("image/")) { toast.error("PNG/JPG only"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.file_id);
      toast.success("Photo uploaded — remember to Save profile");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) doUpload(f); }}
        data-testid="profile-avatar-input"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="border border-zinc-300 px-3 py-1.5 text-xs hover:border-zinc-900 disabled:opacity-50"
          data-testid="profile-avatar-upload-btn"
        >
          {uploading ? "Uploading…" : fileId ? "Replace photo" : "Upload from device"}
        </button>
        {fileId && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="border border-zinc-300 px-3 py-1.5 text-xs text-red-700 hover:border-red-500"
            data-testid="profile-avatar-remove-btn"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
